import React from 'react'
import { createRoot } from 'react-dom/client'
import classNames from 'classnames'
import PerfectScrollbar from 'libs/react-perfect-scrollbar'
import { OmTabs } from '@owma/kit'
import dayjs from 'dayjs'
import { getAppServices } from 'env'
import { store as reduxStore } from 'index'

import styles from './style.module.scss'
import defaultAvatar from 'assets/images/contacts/contacts-white-64.svg'
import favoriteOnIcon from 'assets/images/contacts/favorite-fill-24.svg'
import favoriteOffIcon from 'assets/images/contacts/favorite-stroke-24.svg'
import phoneIcon from 'assets/images/contacts/property-phone-20.svg'
import emailIcon from 'assets/images/contacts/property-email-20.svg'
import addressIcon from 'assets/images/contacts/property-location-20.svg'
import linkIcon from 'assets/images/contacts/property-link-20.svg'
import anniversaryIcon from 'assets/images/contacts/property-anniversary-20.svg'
import relationIcon from 'assets/images/contacts/property-relation-20.svg'
import customIcon from 'assets/images/contacts/property-custom-20.svg'
import notesIcon from 'assets/images/contacts/property-notes-20.svg'
import loading from 'assets/images/gif/loading.gif'
import historyIcon from 'assets/images/contacts/history-28.svg'
import { getColorByString, confirmBase } from 'utils/ui'
import orgChartHelper from 'pages/contacts/components/OrgChart/Helper'
import contactHelper from 'pages/contacts/components/SharedContacts/Helper'
import DetailData from '../DetailData'
import i18n from 'i18n'
import SharedContactEditor from 'pages/contacts/components/SharedContacts/Editor'
import * as contactService from 'pages/contacts/modules/contactService'
import { NameObj, NameSplitter } from 'pages/contacts/utils/NameSplitter'
import * as dateUtils from 'pages/calendar/utils/DateUtils'

interface Phone {
  type: string
  number: string
}

interface Email {
  type: string
  email: string
}

interface Address {
  type: string
  street: string
  po_box?: string
  city?: string
  post_code?: string
  state?: string
  country?: string
}

interface Link {
  type: string
  url: string
}

interface DateItem {
  type: string
  date: string
}

interface RelatedPerson {
  type: string
  name: string
}

interface CustomField {
  label: string
  content: string
}

interface Note {
  content: string
  creator?: string
  updated_at?: number
}

interface CategoryTag {
  id: string
  name: string
  color: string
}

interface ContactInfo {
  id?: string
  name?: string
  email?: string
  phone?: string
  deleted?: boolean
  [key: string]: any
}

interface HistoryItem {
  provider_display_name: string
  provider_email: string
  content_diff: string
  target_action: string
  created_at: number
}

interface ContactDetailProps {
  info: ContactInfo
  type: 'public' | 'organization' | 'personal' | 'byEmail'
  isTrash?: boolean
  onEdit?: (data: any, color: string) => void
  getModalObj?: () => any
}

interface ContactDetailState {
  detailData: any
  photoUrl: string | null
  historyData: HistoryItem[] | null
  historyLabelWidth: string | number
  activeTab: string
  profileColor: string
  orgDetailData: any
  sharedDetailData: any
  canEdit: boolean
  noteAuthorNames: Record<string, string>
}

class ContactDetail extends React.Component<ContactDetailProps, ContactDetailState> {
  state: ContactDetailState = {
    detailData: null,
    photoUrl: null,
    historyData: null,
    historyLabelWidth: '100%',
    activeTab: 'contact',
    profileColor: '#ac725e',
    orgDetailData: null,
    sharedDetailData: null,
    canEdit: false,
    noteAuthorNames: {},
  }

  t = i18n.t

  assignedModalObj: boolean = false
  containerRef = React.createRef<HTMLDivElement>()
  photoRef = React.createRef<HTMLImageElement>()
  noteAuthorNames: Record<string, string> = {}

  componentDidMount() {
    this.initDetail()

    const { role } = reduxStore.getState().user
    if (role === 'admin' || role === 'owner') {
      this.setState({ canEdit: true })
    } else {
      const { crm_contacts } = getAppServices()
      const { editable_by_user } = crm_contacts
      if (editable_by_user) {
        this.setState({ canEdit: true })
      }
    }
  }

  componentDidUpdate(prevProps: ContactDetailProps) {
    const { info } = this.props

    if (prevProps != null && prevProps.info !== info) {
      setTimeout(() => {
        this.initDetail()
      }, 100)
    }

    if (this.containerRef.current && this.state.historyLabelWidth === '100%') {
      this.setState({
        historyLabelWidth: this.containerRef.current.clientWidth - 28 - 8 - 32,
      })
      // 28 : history icon width
      // 8 : gap
      // 32 : Container Padding 16 * 2
    }

    const modalObj = this.props.getModalObj && this.props.getModalObj()
    if (modalObj && !this.assignedModalObj) {
      const closeButton = document.getElementById('contact-detail-modal-edit-button')
      if (closeButton) {
        this.assignedModalObj = true

        closeButton.addEventListener('click', () => {
          modalObj.destroy()

          const editorDiv = document.createElement('div')
          document.body.appendChild(editorDiv)
          const editorRoot = createRoot(editorDiv)
          editorRoot.render(
            <SharedContactEditor
              onClose={
                (/* addedNew = false */) => {
                  editorRoot.unmount()
                  editorDiv.remove()
                }
              }
              initialDetailData={this.state.detailData}
            />,
          )
        })
      }
    }
  }

  searchContact = async (info: ContactInfo, type: string) => {
    const orgData = await orgChartHelper.findContact(info.email, false)
    let orgDetailData = null
    if (orgData != null) {
      const { department, job, jobposition } = await orgChartHelper.getSubData(orgData.id)
      orgData.departmentData = {
        title: department?.name,
        id: department?.id,
        position: department?.position,
      }
      orgData.jobData = {
        jobtitle: job?.name || '',
        position: job?.position || '',
        jobposition: jobposition?.name || '',
        jobposition_id: jobposition?.id || null,
      }

      orgDetailData = new DetailData(orgData, 'organization')
      this.setState({ orgDetailData })
    }

    let sharedDetailData = null
    const sharedData = await contactHelper.findContact(info.email, false)
    if (sharedData != null) {
      sharedDetailData = new DetailData(sharedData, 'public')
      this.setState({ sharedDetailData })
    }

    let detailData = null
    if (orgDetailData != null || sharedDetailData != null) {
      detailData = DetailData.merge(orgDetailData, sharedDetailData, info)
      this.setState({ detailData })
    } else {
      detailData = new DetailData(info, type)
      this.setState({ detailData })
    }

    this.setState({ activeTab: 'overview', photoUrl: detailData.photoUrl || null })
  }

  initDetail = () => {
    const { info, type } = this.props

    if (type === 'public' && info != null && info.id != null) {
      const { id, deleted } = info

      this.loadData(id, deleted)
        .then((res: any) => {
          console.log('contact detail shared loadData:', res)
          const data = res.data.data
          const detailData = new DetailData(data, type)

          const photoUrl = data?.photo?.url || null
          this.setState({ detailData, photoUrl: photoUrl })
          if (this.photoRef.current) {
            this.photoRef.current.src = photoUrl
          }
          this.setState({
            profileColor: getColorByString(
              detailData.fullName ||
                detailData.email ||
                detailData.phone ||
                i18n.t('contact.menu.noName'),
            ),
          })

          detailData.notes?.forEach((note: Note | any, index: number) => {
            if (note.creator) {
              orgChartHelper.findContact(note.creator, false).then((user: any) => {
                if (user?.display_name) {
                  this.noteAuthorNames[index.toString()] = user.display_name
                  this.setState({
                    noteAuthorNames: { ...this.noteAuthorNames },
                  })
                }
              })
            }
          })
        })
        .catch((err: any) => {
          console.error('contact detail shared loadData error:', err)
        })

      contactHelper.getHistory({ id }).then((res: any) => {
        console.log('loadHistory:', res)
        const historyData = res.data.data
        this.setState({ historyData })
      })

      this.setState({ activeTab: 'contact' })
    } else if (type === 'organization') {
      const detailData = new DetailData(info, type)

      this.setState({
        detailData,
        photoUrl: detailData.photoUrl,
        profileColor: getColorByString(
          detailData.fullName ||
            detailData.email ||
            detailData.phone ||
            i18n.t('contact.menu.noName'),
        ),
      })
    } else if (type === 'personal') {
      const detailData = new DetailData(info, type)
      this.setState({
        detailData,
        photoUrl: detailData.photoUrl || null,
        profileColor: getColorByString(
          detailData.fullName ||
            detailData.email ||
            detailData.phone ||
            i18n.t('contact.menu.noName'),
        ),
      })
      if (detailData.hasPhoto) {
        const fetchPhoto = async () => {
          const p = await contactService.getPhoto(detailData.id)
          if (p && p.photo) {
            let photoUrl = p.photo
            if (!photoUrl.startsWith('data:image/')) {
              photoUrl = `data:image/png;base64,${photoUrl}`
            }
            this.setState({ photoUrl })
            detailData.photoUrl = photoUrl
          }
        }
        fetchPhoto()
      }
    } else {
      if (info.email && info.email.length > 0) {
        this.searchContact(info, type)
      } else {
        const detailData = new DetailData(info, type)
        this.setState({ detailData })
      }
    }
  }

  loadData = async (id: string, deleted?: boolean) => {
    const res = await contactHelper.getContact(id, deleted)
    return res
  }

  handleFavorite = () => {
    const { id, favorite } = this.state.detailData
    this.setState({ detailData: { ...this.state.detailData, favorite: !favorite } })
    contactHelper.setFavorite(id, !favorite)
  }

  handleEdit = () => {
    if (this.props.type === 'personal') {
      this.props.onEdit?.(this.state.detailData, this.state.profileColor)
    } else {
      this.props.onEdit?.(this.state.detailData.sharedContactData, this.state.profileColor)
    }
  }

  renderHeader = (detailData: any) => {
    const { email, orgInfo, categoryTags, fullName, phone } = detailData
    const t = i18n.t
    const nameObj = new NameObj({
      prefix: detailData.prefix,
      first_name: detailData.firstName,
      last_name: detailData.lastName,
      middle_name: detailData.middleName,
      suffix: detailData.suffix,
    })
    const nameSplitter = new NameSplitter(navigator.language || 'en-US')
    nameSplitter.guessFullNameStyle(nameObj)
    let displayName = nameSplitter.joinWithName(nameObj, true, true)
    const { yomiFirstName, yomiLastName, yomiMiddleName } = detailData

    if (displayName == null || displayName.length === 0) {
      displayName = `${yomiFirstName ? `${yomiFirstName} ` : ''}${yomiMiddleName ? `${yomiMiddleName} ` : ''}${yomiLastName ? `${yomiLastName}` : ''}`
    }

    const hasCategories = categoryTags && categoryTags.length > 0
    const categoryTagsElements = hasCategories
      ? categoryTags.map((cat: CategoryTag) => (
          <div
            key={cat.id}
            className={styles.tag}
            style={
              {
                '--category-color': '#' + cat.color,
                '--category-bg-color': '#' + cat.color + '22',
              } as React.CSSProperties
            }
          >
            {cat.name}
          </div>
        ))
      : null

    return (
      <div className={styles.header}>
        {this.state.photoUrl ? (
          <div className={styles.profileImage}>
            <img
              src={this.state.photoUrl}
              alt="Profile"
              onError={() => {
                this.setState({ photoUrl: null })
              }}
              ref={this.photoRef}
            />
          </div>
        ) : (
          <div
            className={styles.profileImage}
            style={{
              backgroundColor: this.state.profileColor,
            }}
          >
            <img src={defaultAvatar} alt="Profile" style={{ width: '64px', height: '64px' }} />
          </div>
        )}
        <div className={styles.headerTextInfo}>
          <div className={styles.basicInfo}>
            <div className={styles.nameContainer}>
              <h2 className={styles.name}>
                {fullName || email || phone || t('contact.menu.noName')}
              </h2>
              {this.props.type === 'public' && !this.props.isTrash && (
                <button className={styles.favoriteButton} onClick={this.handleFavorite}>
                  {this.state.detailData.favorite ? (
                    <img src={favoriteOnIcon} alt="favorite" />
                  ) : (
                    <img src={favoriteOffIcon} alt="favorite" />
                  )}
                </button>
              )}
            </div>
            {displayName && <div className={styles.displayName}>{displayName}</div>}
            {orgInfo && <div className={styles.organization}>{orgInfo}</div>}
            {categoryTagsElements && <div className={styles.tags}>{categoryTagsElements}</div>}
          </div>
          {((this.props.type === 'public' && !this.props.isTrash) ||
            this.props.type === 'personal') &&
            this.props.onEdit && (
              <div className={styles.editButtonContainer}>
                <button
                  className={styles.editButton}
                  onClick={this.handleEdit}
                  disabled={!this.state.canEdit}
                >
                  {t('contact.edit.editContact')}
                </button>
              </div>
            )}
        </div>
      </div>
    )
  }

  renderSection = (
    icon: React.ReactNode,
    children: React.ReactNode,
    index: number | null = null,
  ) => {
    const aStyle = index && index > 0 ? { marginTop: '0px' } : {}
    return (
      <div className={styles.section} style={aStyle}>
        <div className={styles.sectionIcon}>{icon}</div>
        <div className={styles.sectionContent}>{children}</div>
      </div>
    )
  }

  getPhoneTypeDesc = (type: string) => {
    const t = i18n.t
    const oldType = type
    type = type?.toLowerCase() || ''
    if (type === 'mobile') return t('contact.detail.mobile')
    if (type === 'work') return t('contact.detail.work')
    if (type === 'home') return t('contact.detail.home')
    if (type === 'fax') return t('contact.property.fax')
    if (type === 'pager') return t('contact.property.pager')
    if (type === 'other') return t('contact.detail.other')
    return type.length > 0 ? oldType : t('contact.detail.phone')
  }

  renderPhones = (phones: Phone[]) => {
    if (!phones || phones.length === 0) return null

    return phones.map((phone, index) => (
      <div key={index} className={styles.detailItem}>
        <div className={styles.detailType}>{this.getPhoneTypeDesc(phone.type)}</div>
        <div className={styles.detailValue}>{phone.number}</div>
      </div>
    ))
  }

  getEmailTypeDesc = (type: string) => {
    const t = i18n.t
    const oldType = type
    type = type?.toLowerCase() || ''
    if (type === 'work') return t('contact.detail.work')
    if (type === 'other') return t('contact.detail.other')
    if (type === 'home') return t('contact.detail.home')
    if (type === 'personal') return t('contact.title.personal')
    return type.length > 0 ? oldType : t('contact.detail.email')
  }

  renderEmails = (emails: Email[]) => {
    if (!emails || emails.length === 0) return null

    return emails.map((email, index) => (
      <div key={index} className={styles.detailItem}>
        <div className={styles.detailType}>{this.getEmailTypeDesc(email.type)}</div>
        <div className={styles.detailValue}>
          <a href={`mailto:${email.email}`} target="_blank" rel="noopener noreferrer">
            {email.email}
          </a>
        </div>
      </div>
    ))
  }

  getAddressTypeDesc = (type: string) => {
    const t = i18n.t
    const oldType = type
    type = type?.toLowerCase() || ''
    if (type === 'home') return t('contact.detail.home')
    if (type === 'work') return t('contact.detail.work')
    if (type === 'other') return t('contact.detail.other')
    return type.length > 0 ? oldType : t('contact.detail.address')
  }

  renderAddresses = (addresses: Address[]) => {
    if (!addresses || addresses.length === 0) return null

    return addresses.map((address, index) => (
      <div key={index} className={styles.detailItem}>
        <div className={styles.detailType}>{this.getAddressTypeDesc(address.type)}</div>
        <div className={styles.detailValue}>
          {address.street}
          {address.po_box && <div>{address.po_box}</div>}
          {address.city && (
            <div>
              {address.city} {address.post_code}
            </div>
          )}
          {address.state && <div>{address.state}</div>}
          {address.country && <div>{address.country}</div>}
        </div>
      </div>
    ))
  }

  getLinkTypeDesc = (type: string) => {
    const t = i18n.t
    const oldType = type
    type = type?.toLowerCase() || ''
    if (type === 'profile') return t('contact.detail.profile')
    if (type === 'work') return t('contact.detail.work')
    if (type === 'blog') return t('contact.detail.blog')
    if (type === 'website') return t('contact.property.website')
    if (type === 'linkedin') return t('contact.property.linkedin')
    if (type === 'facebook') return t('contact.property.facebook')
    if (type === 'github') return t('contact.property.github')
    if (type === 'skype') return t('contact.property.skype')
    if (type === 'twitter') return t('contact.property.twitter')
    if (type === 'whatsapp') return t('contact.property.whatsapp')
    if (type === 'instagram') return t('contact.property.instagram')
    if (type === 'other') return t('contact.property.other')
    return type.length > 0 ? oldType : t('contact.detail.link')
  }

  // URL에 프로토콜이 없으면 https:// 자동 추가
  ensureProtocol = (url: string) => {
    if (!url) return ''
    return url.match(/^https?:\/\//) ? url : `https://${url}`
  }

  renderLinks = (links: Link[]) => {
    if (!links || links.length === 0) return null

    return links.map((link, index) => (
      <div key={index} className={styles.detailItem}>
        <div className={styles.detailType}>{this.getLinkTypeDesc(link.type)}</div>
        <div className={styles.detailValue}>
          <a href={this.ensureProtocol(link.url)} target="_blank" rel="noopener noreferrer">
            {link.url}
          </a>
        </div>
      </div>
    ))
  }

  getDateTypeDesc = (type: string) => {
    const t = i18n.t
    const oldType = type
    type = type?.toLowerCase() || ''
    if (type === 'birthday') return t('contact.property.birthday')
    if (type === 'anniversary') return t('contact.property.anniversary')
    if (type === 'other') return t('contact.property.other')
    return type.length > 0 ? oldType : t('contact.detail.date')
  }

  renderDates = (dates: DateItem[]) => {
    if (!dates || dates.length === 0) return null

    return dates.map((date, index) => (
      <div key={index} className={styles.detailItem}>
        <div className={styles.detailType}>{this.getDateTypeDesc(date.type)}</div>
        <div className={styles.detailValue}>{date.date}</div>
      </div>
    ))
  }

  getRelatedPersonTypeDesc = (type: string) => {
    const t = i18n.t
    const oldType = type
    type = type?.toLowerCase() || ''
    if (type === 'spouse') return t('contact.detail.spouse')
    if (type === 'child') return t('contact.detail.child')
    if (type === 'mother') return t('contact.detail.mother')
    if (type === 'father') return t('contact.detail.father')
    if (type === 'parent') return t('contact.detail.parent')
    if (type === 'sibling') return t('contact.property.sibling')
    if (type === 'brother') return t('contact.detail.brother')
    if (type === 'sister') return t('contact.property.sister')
    if (type === 'friend') return t('contact.property.friend')
    if (type === 'relative') return t('contact.property.relative')
    if (type === 'manager') return t('contact.property.manager')
    if (type === 'assistant') return t('contact.property.assistant')
    if (type === 'referredby') return t('contact.property.referredby')
    if (type === 'partner') return t('contact.property.partner')
    if (type === 'domesticpartner' || type === 'domestic')
      return t('contact.property.domesticpartner')
    return type.length > 0 ? oldType : t('contact.detail.person')
  }

  renderRelatedPersons = (persons: RelatedPerson[]) => {
    if (!persons || persons.length === 0) return null

    return persons.map((person, index) => (
      <div key={index} className={styles.detailItem}>
        <div className={styles.detailType}>{this.getRelatedPersonTypeDesc(person.type)}</div>
        <div className={styles.detailValue}>{person.name}</div>
      </div>
    ))
  }

  renderCustomFields = (customs: CustomField[]) => {
    if (!customs || customs.length === 0) return null

    return customs.map((custom, index) => (
      <div key={index} className={styles.detailItem}>
        <div className={styles.detailType}>{custom.label}</div>
        <div className={styles.detailValue}>{custom.content}</div>
      </div>
    ))
  }

  renderNotes = (note: Note, index: number) => {
    if (!note || !note.content || note.content.length === 0) return null
    const t = i18n.t

    const noteLines = note.content.split('\n')
    const noteAuthorName = this.noteAuthorNames[index.toString()]
    let dateStr = null

    const date = note.updated_at ? new Date(note.updated_at * 1000) : null

    if (date && !isNaN(date.getTime())) {
      const today = new Date()
      const betweenDay = Math.abs(dateUtils.betweenDay(today, date))

      if (betweenDay === 0) {
        dateStr = dateUtils.localizedTimeString(date)
      } else if (betweenDay <= 7) {
        dateStr = dateUtils.localizedDayString(date) + ' ' + dateUtils.localizedTimeString(date)
      } else {
        dateStr = dateUtils.localizedDateString(date)
      }
    }

    return (
      <div key={index} className={styles.note}>
        <div className={styles.noteTitle}>
          {t('contact.detail.memo')}
          {dateStr ? <div className={styles.noteTitleDate}>{dateStr}</div> : ''}
          {noteAuthorName ? ` • ${noteAuthorName}` : ''}
        </div>
        <div className={styles.noteContent}>
          {noteLines.map((line, lineIndex) => (
            <React.Fragment key={lineIndex}>
              {line}
              <br />
            </React.Fragment>
          ))}
        </div>
      </div>
    )
  }

  renderDetailContents = (detailData: any) => {
    // const t = i18n.t

    return (
      <div className={styles.detailsContainer}>
        {detailData.phones.length > 0 &&
          this.renderSection(
            <img src={phoneIcon} alt="phone" />,
            this.renderPhones(detailData.phones),
          )}

        {detailData.emails.length > 0 &&
          this.renderSection(
            <img src={emailIcon} alt="email" />,
            this.renderEmails(detailData.emails),
          )}

        {detailData.addresses.length > 0 &&
          this.renderSection(
            <img src={addressIcon} alt="address" />,
            this.renderAddresses(detailData.addresses),
          )}

        {detailData.links.length > 0 &&
          this.renderSection(<img src={linkIcon} alt="link" />, this.renderLinks(detailData.links))}

        {detailData.dates.length > 0 &&
          this.renderSection(
            <img src={anniversaryIcon} alt="anniversary" />,
            this.renderDates(detailData.dates),
          )}

        {detailData.relatedPersons.length > 0 &&
          this.renderSection(
            <img src={relationIcon} alt="relation" />,
            this.renderRelatedPersons(detailData.relatedPersons),
          )}

        {detailData.customFields.length > 0 &&
          this.renderSection(
            <img src={customIcon} alt="custom" />,
            this.renderCustomFields(detailData.customFields),
          )}

        {detailData.notes.length > 0 &&
          detailData.notes.map((note: Note, index: number) => (
            <div key={index}>
              {this.renderSection(
                <img className={styles.noteIcon} src={notesIcon} alt="note" />,
                this.renderNotes(note, index),
                index,
              )}
            </div>
          ))}
      </div>
    )
  }

  historyTag = (tag: string, subTag: string | null = null) => {
    const t = i18n.t
    if (tag === 'person') {
      if (!subTag || subTag === 'display_name') {
        return t('contact.menu.name')
      } else if (subTag === 'first_name') {
        return t('contact.property.firstName')
      } else if (subTag === 'last_name') {
        return t('contact.property.lastName')
      } else if (subTag === 'middle_name') {
        return t('contact.property.middleName')
      } else if (subTag === 'prefix') {
        return t('contact.property.prefix')
      } else if (subTag === 'suffix') {
        return t('contact.property.suffix')
      } else if (subTag === 'nickname') {
        return t('contact.property.nickname')
      } else if (subTag === 'yomifirstname') {
        return t('contact.property.phoneticFirstName')
      } else if (subTag === 'yomilastname') {
        return t('contact.property.phoneticLastName')
      }
    } else if (tag === 'organization') {
      return t('contact.detail.organization')
    } else if (tag === 'phones') {
      return t('contact.detail.phone')
    } else if (tag === 'emails') {
      return t('contact.detail.email')
    } else if (tag === 'links') {
      return t('contact.detail.link')
    } else if (tag === 'favorites') {
      return t('contact.detail.favorite')
    } else if (tag === 'category') {
      return t('contact.tree.category')
    } else if (tag === 'addresses') {
      return t('contact.detail.address')
    } else if (tag === 'event_dates') {
      return t('contact.detail.eventdate')
    } else if (tag === 'related_persons') {
      return t('contact.detail.relation')
    } else if (tag === 'photo') {
      return t('contact.detail.photo')
    } else if (tag === 'customs') {
      return t('contact.property.custom')
    } else if (tag === 'notes') {
      return t('contact.detail.memo')
    }
    return null
  }

  renderHistories = () => {
    const { historyData } = this.state
    const t = i18n.t

    if (!historyData || historyData.length === 0) {
      return null
    }

    return (
      <div className={styles.historyContainer}>
        {historyData.map((history, index) => {
          const { provider_display_name, provider_email, content_diff, target_action, created_at } =
            history
          let name = provider_display_name
          if (name === null || name === '') {
            name = provider_email.split('@')[0]
          }

          const date = dayjs(created_at * 1000)
          let dateStr = null
          if (date.isSame(dayjs(), 'day')) {
            dateStr = date.format('HH:mm')
          } else {
            dateStr = date.format('YYYY-MM-DD')
          }

          const changedTargets = new Set<string>()
          let firstLine = null

          let diffArr: any = null
          if (content_diff) {
            diffArr = JSON.parse(content_diff)
          }

          if (Array.isArray(diffArr) && diffArr?.length > 0) {
            diffArr.forEach((diff: any) => {
              const { path } = diff
              const tag = this.historyTag(path[0], path[1])
              if (tag) {
                changedTargets.add(tag)
              }
            })

            const text = Array.from(changedTargets).join(', ')

            if (target_action === 'update') {
              firstLine = t('contact.history.updated', { text: text })
            } else if (target_action === 'merge') {
              firstLine = t('contact.history.merged', { text: text })
            } else if (target_action === 'delete') {
              firstLine = t('contact.history.deleted', { text: text })
            } else if (target_action === 'restore') {
              firstLine = t('contact.history.restored', { text: text })
            } else {
              firstLine = text
            }
          } else if (target_action === 'add') {
            firstLine = t('contact.history.created')
          }

          return (
            <div key={index} className={styles.historyItem}>
              <img src={historyIcon} alt="history" />
              <div className={styles.historyItemContent}>
                <div
                  className={styles.historyItemTitle}
                  style={{ width: this.state.historyLabelWidth }}
                >
                  {firstLine}
                </div>
                <div className={styles.historySecondaryText}>{name + ', ' + dateStr}</div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  render() {
    const { detailData } = this.state
    const t = i18n.t
    if (!detailData) {
      return (
        <div className={styles.loading}>
          <img src={loading} alt="loading" />
        </div>
      )
    }

    const items = [
      {
        key: 'contact',
        label: <span>{t('contact.tab.contact')}</span>,
        children: this.renderDetailContents(detailData),
      },
      {
        key: 'history',
        label: <span>{t('contact.tab.history')}</span>,
        children: <div className={styles.historyContainer}>{this.renderHistories()}</div>,
      },
    ]

    const byEmailItems = [
      {
        key: 'overview',
        label: <span>{t('contact.tab.overview')}</span>,
        children: this.renderDetailContents(detailData),
      },
    ]
    if (this.state.sharedDetailData) {
      byEmailItems.push({
        key: 'shared',
        label: <span>{t('contact.tab.shared')}</span>,
        children: this.renderDetailContents(this.state.sharedDetailData),
      })

      const editButton = document.getElementById('contact-detail-modal-edit-button')
      if (editButton) {
        editButton.style.display = 'none'
      }
    }

    return (
      <PerfectScrollbar
        className={styles.scrollView}
        height="100%"
        options={{ suppressScrollX: true }}
      >
        <div className={classNames(styles.contactDetail)} ref={this.containerRef}>
          {this.renderHeader(detailData)}

          {this.props.type === 'public' && (
            <OmTabs
              className="custom-tabs-nav"
              items={items}
              activeKey={this.state.activeTab}
              onChange={(key) => this.setState({ activeTab: key })}
            />
          )}
          {(this.props.type === 'organization' || this.props.type === 'personal') && (
            <div className={styles.organizationDetail}>{this.renderDetailContents(detailData)}</div>
          )}
          {this.props.type === 'byEmail' && (
            <OmTabs
              className={styles.tabs}
              items={byEmailItems}
              activeKey={this.state.activeTab}
              onChange={(key) => this.setState({ activeTab: key })}
            />
          )}
        </div>
      </PerfectScrollbar>
    )
  }
}

export default ContactDetail

export function modalContactDetail(name: string, email: string) {
  const modalObj = confirmBase({
    wrapClassName: styles.contactDetailPopup,
    width: 600,
    closable: false,
    content: <ContactDetail info={{ name, email }} type="byEmail" getModalObj={() => modalObj} />,
    footer: (
      <div className={styles.footer} id="contact-detail-modal-footer">
        <div className={styles.wrapEditButton}>
          <button id="contact-detail-modal-edit-button" className={styles.editButton}>
            {i18n.t('contact.menu.addContact')}
          </button>
        </div>
        <button className={styles.closeButton} onClick={() => modalObj.destroy()}>
          {i18n.t('common.button.close')}
        </button>
      </div>
    ),
  })

  return modalObj
}

export function modalContactDetailSharedContactId(id: string) {
  const modalObj = confirmBase({
    wrapClassName: styles.contactDetailPopup,
    width: 600,
    closable: false,
    content: <ContactDetail info={{ id }} type="public" getModalObj={() => modalObj} />,
    footer: (
      <div className={styles.footer} id="contact-detail-modal-footer">
        <div className={styles.wrapEditButton}></div>
        <button className={styles.closeButton} onClick={() => modalObj.destroy()}>
          {i18n.t('common.button.close')}
        </button>
      </div>
    ),
    zIndex: 1100,
  })

  return modalObj
}

export function modalContactDetailPersonal(contactData: any) {
  const modalObj = confirmBase({
    wrapClassName: styles.contactDetailPopup,
    width: 600,
    closable: false,
    content: <ContactDetail info={contactData} type="personal" getModalObj={() => modalObj} />,
    footer: (
      <div className={styles.footer} id="contact-detail-modal-footer">
        <div className={styles.wrapEditButton}></div>
        <button className={styles.closeButton} onClick={() => modalObj.destroy()}>
          {i18n.t('common.button.close')}
        </button>
      </div>
    ),
    zIndex: 1100,
  })

  return modalObj
}
